import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Mic, MessageCircle, Smile, ChevronDown, Reply, X, FileText, ChevronRight, Trash2, Copy } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { Link } from 'react-router-dom';
import { Avatar } from '../Profile/Avatar';
import { VoiceRecorder } from './VoiceRecorder';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';
import { toast } from 'sonner';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';

// Detect conversation post links and render as embed cards
function renderMessageContent(content: string) {
  const convoPostRegex = /(https?:\/\/[^\s]+\/conversations\/post\/[^\s]+)/g;
  const match = content.match(convoPostRegex);
  
  if (match && match.length > 0) {
    const url = match[0];
    const textBeforeUrl = content.split(url)[0].trim();
    const title = textBeforeUrl.replace(/^📝\s*/, '').trim();
    const slug = url.split('/conversations/post/').pop() || '';
    const path = `/conversations/post/${slug}`;
    
    return (
      <Link to={path} className="block max-w-xs">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-accent-text/30 transition-colors">
          <div className="bg-accent-text/5 px-3 py-2 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-accent-text" />
            <span className="text-xs font-medium text-accent-text">Conversation Post</span>
          </div>
          <div className="px-3 py-2.5">
            {title && <p className="text-sm font-medium text-content line-clamp-2 mb-1">{title}</p>}
            <div className="flex items-center gap-1 text-xs text-accent-text">
              <span>View post</span>
              <ChevronRight className="h-3 w-3" />
            </div>
          </div>
        </div>
      </Link>
    );
  }
  
  return <span className="whitespace-pre-wrap break-words">{content}</span>;
}

export interface Message {
  id: string;
  content: string;
  messageType?: 'text' | 'voice' | 'file';
  audioUrl?: string;
  audioDuration?: number;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  created_at: string;
  sender: {
    id: string;
    username: string;
    avatar_url?: string;
    isOnline: boolean;
    isCurrentUser?: boolean;
  };
  timestamp: string;
  status: 'sent' | 'delivered' | 'read';
  replyTo?: {
    id: string;
    content: string;
    senderUsername: string;
  };
}

interface ChatCardProps {
  initialMessages?: Message[];
  onSendMessage?: (message: string, replyToId?: string) => void;
  onSendVoiceMessage?: (audioBlob: Blob, duration: number) => Promise<void>;
  onLoadMore?: (oldestMessageCreatedAt: string | null) => Promise<Message[]>;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  className?: string;
  initialMessage?: string | null;
}

export function ChatCard({
  initialMessages = [],
  onSendMessage,
  onSendVoiceMessage,
  onLoadMore,
  onDeleteMessage,
  className,
  initialMessage,
}: ChatCardProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState(initialMessage || '');
  const [hasMore, setHasMore] = useState(initialMessages.length >= 30);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [contextMenu, setContextMenu] = useState<{ message: Message; x: number; y: number } | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number; message: Message } | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileMenuMessage, setMobileMenuMessage] = useState<Message | null>(null);
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => { setContextMenu(null); setShowMobileMenu(false); };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleContextMenu = (e: React.MouseEvent, message: Message) => {
    // Only show context menu on desktop, not on touch devices
    if ('ontouchstart' in window) return;
    e.preventDefault();
    setContextMenu({ message, x: e.clientX, y: e.clientY });
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
    setContextMenu(null);
  };

  // Touch handlers for swipe-to-reply on mobile
  const handleTouchStart = (e: React.TouchEvent, message: Message) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY, time: Date.now(), message });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const timeDiff = Date.now() - touchStart.time;

    // Detect swipe left (negative deltaX) - more than 50px horizontal movement, less vertical
    if (deltaX < -50 && deltaY < 50 && timeDiff < 500) {
      // Swipe left - trigger reply
      handleReply(touchStart.message);
      e.preventDefault();
    }

    setTouchStart(null);
  };

  useEffect(() => {
    setMessages(initialMessages);
    setHasMore(initialMessages.length >= 30);
    setShouldScrollToBottom(true);
  }, [initialMessages]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container && shouldScrollToBottom) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    }
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPosition = scrollHeight - scrollTop - clientHeight;
      const isNearBottomNow = scrollPosition < 100;

      setIsNearBottom(isNearBottomNow);
      setShouldScrollToBottom(isNearBottomNow);
      setShowScrollButton(!isNearBottomNow && scrollPosition > 300);

      if (scrollTop === 0 && hasMore && !loadingMore) {
        const currentScroll = container.scrollHeight - container.scrollTop;
        handleLoadMore().then(() => {
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - currentScroll;
          });
        });
      }
    }
  };

  const handleReply = useCallback((message: Message) => { setReplyingTo(message); }, []);
  const cancelReply = useCallback(() => { setReplyingTo(null); }, []);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore || !onLoadMore || messages.length === 0) return;
    try {
      setLoadingMore(true);
      const oldestMessage = messages[0];
      const olderMessages = await onLoadMore(oldestMessage?.created_at || null);
      if (olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        setHasMore(olderMessages.length >= 30);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isNewMessage = lastMessage && Date.now() - new Date(lastMessage.created_at).getTime() < 1000;
      if (isNewMessage || initialMessages === messages) {
        scrollToBottom('auto');
        setShouldScrollToBottom(true);
      }
    }
  }, [messages, initialMessages]);

  // Scroll to bottom on initial mount only
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      scrollToBottom('auto');
      setShouldScrollToBottom(true);
    }
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      let scrollTimeout: NodeJS.Timeout;
      const handleScrollThrottled = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => { if (!loadingMore) handleScroll(); }, 100);
      };
      container.addEventListener('scroll', handleScrollThrottled);
      return () => { container.removeEventListener('scroll', handleScrollThrottled); if (scrollTimeout) clearTimeout(scrollTimeout); };
    }
  }, [loadingMore]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    try {
      await onSendMessage?.(inputValue);
      setInputValue('');
      if (inputRef.current) inputRef.current.focus();
      setShouldScrollToBottom(true);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // Group messages by sender and time
  const groupedMessages = messages.reduce((groups: Message[][], message, index) => {
    const prevMessage = messages[index - 1];
    const shouldStartNewGroup = 
      !prevMessage || 
      prevMessage.sender.id !== message.sender.id ||
      (new Date(message.timestamp).getTime() - new Date(prevMessage.timestamp).getTime() > 5 * 60 * 1000);
    
    if (shouldStartNewGroup) {
      groups.push([message]);
    } else {
      groups[groups.length - 1].push(message);
    }
    return groups;
  }, []);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Messages area */}
      <div className="flex-1 min-h-0 bg-white relative">
        <div 
          ref={messagesContainerRef}
          className="absolute inset-0 overflow-y-auto px-4 lg:px-6 py-4 space-y-1"
          onScroll={handleScroll}
        >
          {loadingMore && (
            <div className="flex justify-center py-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-text border-t-transparent"></div>
            </div>
          )}
          
          {hasMore && !loadingMore && (
            <button onClick={handleLoadMore} className="w-full text-xs text-accent-text hover:text-accent-text/80 py-2 font-medium">
              Load older messages
            </button>
          )}

          {groupedMessages.map((group, groupIndex) => (
            <div key={`group-${groupIndex}`} className="space-y-0.5">
              {group.map((message, messageIndex) => {
                const isFirstInGroup = messageIndex === 0;
                const isCurrentUser = message.sender.isCurrentUser;

                return (
                  <div
                    key={message.id}
                    className={`group relative flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-3`}
                    onContextMenu={(e) => handleContextMenu(e, message)}
                    onPointerDown={(e) => {
                      if (e.pointerType === 'touch') {
                        longPressTimerRef.current = setTimeout(() => {
                          setMobileMenuMessage(message);
                          setShowMobileMenu(true);
                        }, 500);
                      }
                    }}
                    onPointerUp={(e) => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                    onPointerCancel={(e) => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                    onTouchStart={(e) => handleTouchStart(e, message)}
                    onTouchEnd={(e) => handleTouchEnd(e)}
                  >
                    {/* Avatar - only show for other user's first message in a group */}
                    {!isCurrentUser && isFirstInGroup && (
                      <div className="flex-shrink-0 mr-2 self-end mb-0.5">
                        <Avatar url={message.sender.avatar_url} size="sm" userId={message.sender.id} editable={false} />
                      </div>
                    )}
                    {/* Empty space for alignment when no avatar */}
                    {!isCurrentUser && !isFirstInGroup && <div className="w-9 mr-2" />}

                    {/* Message bubble */}
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2.5 transition-shadow ${
                        isCurrentUser
                          ? 'bg-[#F4F7EE] text-content rounded-br-md'
                          : 'bg-gray-100 text-content rounded-bl-md'
                      } ${contextMenu?.message.id === message.id ? 'shadow-xl ring-2 ring-accent-text/30' : ''}`}
                    >
                      {/* Username - show for other users */}
                      {!isCurrentUser && isFirstInGroup && (
                        <p className="text-xs font-semibold text-accent-text mb-1">{message.sender.username}</p>
                      )}

                      {/* Reply quote */}
                      {message.replyTo && (
                        <div className={`mb-2 pl-2 border-l-2 py-0.5 ${isCurrentUser ? 'border-accent-text/30' : 'border-accent-text/20'}`}>
                          <span className={`text-xs font-medium ${isCurrentUser ? 'text-accent-text' : 'text-accent-text'}`}>
                            {message.replyTo.senderUsername}
                          </span>
                          <p className={`text-xs truncate ${isCurrentUser ? 'text-content/70' : 'text-content/60'}`}>
                            {message.replyTo.content}
                          </p>
                        </div>
                      )}

                      {/* Message content */}
                      <div className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                        {message.messageType === 'voice' && message.audioUrl ? (
                          <VoiceMessagePlayer
                            audioUrl={message.audioUrl}
                            duration={message.audioDuration}
                            className={`${isCurrentUser ? 'bg-white/20' : 'bg-white'} rounded-lg p-2 max-w-[200px]`}
                          />
                        ) : isCurrentUser ? (
                          renderMessageContent(message.content)
                        ) : (
                          renderMessageContent(message.content)
                        )}
                      </div>

                      {/* Small time indicator */}
                      <div className={`text-[10px] mt-1 ${isCurrentUser ? 'text-content/50 text-right' : 'text-content/40'}`}>
                        {message.timestamp}
                      </div>
                    </div>

                    {/* Reply and Delete buttons on hover - desktop only */}
                      <div className="hidden md:flex opacity-0 group-hover:opacity-100 absolute -top-8 right-0 items-center gap-1 p-1 rounded-lg bg-white shadow-sm border border-gray-100 transition-all">
                        <button
                          onClick={() => handleReply(message)}
                          className="p-1.5 rounded-lg text-content/30 hover:text-accent-text hover:border-accent-text/20 transition-all"
                          title="Reply"
                        >
                          <Reply className="h-3.5 w-3.5" />
                        </button>
                        {isCurrentUser && onDeleteMessage && (
                          <button
                            onClick={() => onDeleteMessage(message.id)}
                            className="p-1.5 rounded-lg text-content/30 hover:text-red-500 hover:border-red-200 transition-all"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom */}
        {showScrollButton && (
          <button
            onClick={() => { setShouldScrollToBottom(true); scrollToBottom('smooth'); setShowScrollButton(false); }}
            className="absolute bottom-4 right-4 p-2.5 rounded-xl bg-white shadow-md border border-gray-100 text-content/40 hover:text-accent-text hover:border-accent-text/20 transition-all z-10"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        )}

        {/* Context Menu - larger for mobile with visible labels */}
        {contextMenu && (
          <>
            {/* Backdrop to close menu */}
            <div
              className="fixed inset-0 z-40 md:hidden"
              onClick={() => setContextMenu(null)}
            />
            <div
              className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 min-w-[200px] md:min-w-[160px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
              transform: 'translate(-50%, 0)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { handleReply(contextMenu.message); setContextMenu(null); }}
              className="w-full px-5 py-4 text-left text-base md:text-sm text-content hover:bg-gray-50 flex items-center gap-4 transition-colors"
            >
              <Reply className="h-5 w-5 md:h-4 md:w-4 text-accent-text" />
              <span className="font-medium">Reply</span>
            </button>
            <button
              onClick={() => handleCopy(contextMenu.message.content)}
              className="w-full px-5 py-4 text-left text-base md:text-sm text-content hover:bg-gray-50 flex items-center gap-4 transition-colors"
            >
              <Copy className="h-5 w-5 md:h-4 md:w-4 text-accent-text" />
              <span className="font-medium">Copy</span>
            </button>
            {contextMenu.message.sender.isCurrentUser && onDeleteMessage && (
              <button
                onClick={() => { onDeleteMessage(contextMenu.message.id); setContextMenu(null); }}
                className="w-full px-5 py-4 text-left text-base md:text-sm text-red-500 hover:bg-red-50 flex items-center gap-4 transition-colors border-t border-gray-100"
              >
                <Trash2 className="h-5 w-5 md:h-4 md:w-4" />
                <span className="font-medium">Delete</span>
              </button>
            )}
          </div>
          </>
        )}

        {/* Mobile Bottom Sheet Menu */}
        {showMobileMenu && (
          <>
            <div
              className="fixed inset-0 z-40 lg:hidden"
              onClick={() => setShowMobileMenu(false)}
            />
            <div className="fixed left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl z-50 lg:hidden animate-sheet-up pb-safe">
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="p-4 space-y-2 pb-8">
                <button
                  onClick={() => { handleReply(mobileMenuMessage!); setShowMobileMenu(false); }}
                  className="w-full px-5 py-4 text-left text-base text-content hover:bg-gray-50 flex items-center gap-4 transition-colors rounded-xl"
                >
                  <Reply className="h-5 w-5 text-accent-text" />
                  <span className="font-medium">Reply</span>
                </button>
                <button
                  onClick={() => { handleCopy(mobileMenuMessage?.content || ''); setShowMobileMenu(false); }}
                  className="w-full px-5 py-4 text-left text-base text-content hover:bg-gray-50 flex items-center gap-4 transition-colors rounded-xl"
                >
                  <Copy className="h-5 w-5 text-accent-text" />
                  <span className="font-medium">Copy</span>
                </button>
                {mobileMenuMessage?.sender.isCurrentUser && onDeleteMessage && (
                  <button
                    onClick={() => { onDeleteMessage(mobileMenuMessage!.id); setShowMobileMenu(false); }}
                    className="w-full px-5 py-4 text-left text-base text-red-500 hover:bg-red-50 flex items-center gap-4 transition-colors rounded-xl border-t border-gray-100"
                  >
                    <Trash2 className="h-5 w-5" />
                    <span className="font-medium">Delete</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Chat Input */}
      <div className="flex-shrink-0 pb-20 lg:pb-0 bg-white">
        <ChatInput
          onSendMessage={async (message: string) => {
            if (!message.trim()) return;
            try {
              await onSendMessage?.(message, replyingTo?.id);
              setInputValue('');
              setReplyingTo(null);
              setShouldScrollToBottom(true);
            } catch (err) {
              console.error('Error sending message:', err);
            }
          }}
          onSendVoiceMessage={onSendVoiceMessage}
          replyingTo={replyingTo}
          onCancelReply={cancelReply}
        />
      </div>
    </div>
  );
}

// Chat Input component
interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>;
  onSendVoiceMessage?: (audioBlob: Blob, duration: number) => Promise<void>;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
}

export function ChatInput({ onSendMessage, onSendVoiceMessage, replyingTo, onCancelReply }: ChatInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  useEffect(() => {
    if (replyingTo && textareaRef.current) textareaRef.current.focus();
  }, [replyingTo]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      setInputValue(inputValue.slice(0, start) + emoji + inputValue.slice(end));
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      });
    } else {
      setInputValue(prev => prev + emoji);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || loading) return;

    const messageContent = inputValue.trim();
    setLoading(true);
    try {
      await onSendMessage(messageContent);
      setInputValue('');
      setShowEmojiPicker(false);
      if (onCancelReply) onCancelReply();
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
      requestAnimationFrame(() => { if (textareaRef.current) textareaRef.current.focus(); });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  };

  const handleSendVoiceMessage = async (audioBlob: Blob, duration: number) => {
    if (!onSendVoiceMessage) return;
    try {
      await onSendVoiceMessage(audioBlob, duration);
      setShowVoiceRecorder(false);
    } catch (err) {
      toast.error('Failed to send voice message. Please try again.');
    }
  };

  return (
    <div className="bg-white border-t border-gray-100">
      {/* Reply preview */}
      {replyingTo && (
        <div className="flex items-center justify-between px-4 py-2 bg-accent-text/3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-0.5 h-8 bg-accent-text rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-xs font-semibold text-accent-text">Replying to {replyingTo.sender.username}</span>
              <p className="text-xs text-content/40 truncate">
                {replyingTo.messageType === 'voice' ? '🎤 Voice message' : replyingTo.content}
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancelReply} className="p-1 rounded-md hover:bg-gray-100 text-content/30 hover:text-content/60 transition-colors flex-shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-3 lg:p-4 pb-0 lg:pb-4 bg-white">
        <div className="flex items-end gap-2">
          {/* Voice */}
          {onSendVoiceMessage && (
            <button
              type="button"
              onClick={() => setShowVoiceRecorder(!showVoiceRecorder)}
              disabled={loading}
              className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 text-content/40 hover:text-content/60 transition-colors disabled:opacity-50 flex-shrink-0 flex items-center justify-center"
            >
              {showVoiceRecorder ? <MessageCircle className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
          )}

          {showVoiceRecorder ? (
            <div className="flex-1 flex items-center justify-center">
              <VoiceRecorder onSendVoiceMessage={handleSendVoiceMessage} onCancel={() => setShowVoiceRecorder(false)} disabled={loading} />
            </div>
          ) : (
            <>
              {/* Emoji */}
              <div className="relative" ref={emojiPickerRef}>
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 text-content/40 hover:text-content/60 transition-colors flex-shrink-0 flex items-center justify-center"
                >
                  <Smile className="h-5 w-5" />
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-12 left-0 z-50 shadow-xl rounded-xl overflow-hidden border border-gray-100">
                    <EmojiPicker
                      onEmojiClick={handleEmojiClick}
                      theme={Theme.LIGHT}
                      width={320}
                      height={380}
                      searchPlaceHolder="Search emoji..."
                      previewConfig={{ showPreview: false }}
                    />
                  </div>
                )}
              </div>

              {/* Text input */}
              <div className="flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden focus-within:border-accent-text/40 focus-within:ring-1 focus-within:ring-accent-text/10 transition-all">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="w-full px-4 py-2.5 bg-transparent border-none text-content placeholder:text-content/25 focus:outline-none focus:ring-0 text-sm resize-none leading-normal"
                  disabled={loading}
                  rows={1}
                  style={{ minHeight: '40px', maxHeight: '120px' }}
                />
              </div>

              {/* Send */}
              <button
                type="submit"
                disabled={!inputValue.trim() || loading}
                className="w-10 h-10 rounded-xl bg-accent-text text-white hover:bg-accent-text/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 flex items-center justify-center shadow-sm hover:shadow-md disabled:shadow-none"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
